import React, { useState, useEffect, useRef } from "react";
import "./styles.css"; // Import the CSS file
import Balls from "../../pages/Templates/Loading";
import { AiOutlineClose } from "react-icons/ai";
import { searchAddresses } from "../../services/geocodingService";
import { showErrorToast } from "../../pages/utils";

const GeoSearch = ({
    onBoundingBoxUpdate,
    setAddress,
    address,
    deleteSearchLocation,
    isSimple = false,
    setZipCode,
    className,
    setLatAndLng,
    searchLocation = "",
    clearSearchLocation,
}) => {
    const [inputValue, setInputValue] = useState(address || "");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]); // To store multiple locations
    const [inputFocused, setInputFocused] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [noResults, setNoResults] = useState(false);
    const inputRef = useRef(null);
    const geoSearchRef = useRef(null);
    const suggestionsRef = useRef(null);
    useEffect(() => {
        if (address !== undefined && address !== null) {
            setInputValue(address);
        }
    }, [address]);

    useEffect(() => {
        if (searchLocation && searchLocation !== "") {
            setInputValue(searchLocation);
        }
    }, [searchLocation]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside both input and suggestions list
            const isInsideInput = inputRef.current && inputRef.current.contains(event.target);
            const isInsideSuggestions = suggestionsRef.current && suggestionsRef.current.contains(event.target);

            if (!isInsideInput && !isInsideSuggestions) {
                setInputFocused(false);
                setSuggestions([]);
            }
        };

        // Hide suggestions when clicking on navigation elements
        const handleNavigation = (event) => {
            const isNavElement = event.target.closest('[class*="dropdown"], [class*="menu"], [class*="profile"]');
            if (isNavElement) {
                setSuggestions([]);
                setInputFocused(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("click", handleNavigation);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("click", handleNavigation);
        };
    }, []);

    const handleInputChange = async (event) => {
        const value = event.target.value;
        setInputValue(value);
        if (setAddress) setAddress(value);

        if (value === "") {
            setSuggestions([]);
            setError(null);
            setNoResults(false);
            if (clearSearchLocation) clearSearchLocation();
            return;
        }

        setLoading(true);
        setError(null);
        setNoResults(false);

        try {
            // Use new geocoding service instead of direct Nominatim call
            const results = await searchAddresses(value, 5);

            if (results.length === 0) {
                setNoResults(true);
                setSuggestions([]);
            } else {
                setSuggestions(results);
                setNoResults(false);
            }
        } catch (error) {
            console.error("Error fetching suggestions:", error);
            setError("Unable to search addresses. Please try again.");
            setSuggestions([]);
            setNoResults(false);

            // Show error toast for service errors
            showErrorToast("Address search service temporarily unavailable. Please try again.", {
                autoClose: 5000,
                pauseOnHover: true,
            });
        }

        setLoading(false);
    };

    const handleSelectSuggestion = (suggestion) => {
        if (setLatAndLng) {
            setLatAndLng({ latitude: suggestion.lat, longitude: suggestion.lng });
        }

        const { description, lat, lng } = suggestion;
        const newLocation = { description, place_id: suggestion.place_id, lat, lng };
        setSelectedLocations([...selectedLocations, newLocation]);
        setInputValue(newLocation.description);

        // Update parent component's address state
        if (setAddress) {
            setAddress(newLocation.description);
        }

        if (setZipCode && suggestion.address?.postcode) {
            setZipCode(suggestion.address.postcode);
        }

        // Create a simple bounding box around the point (approximate 0.01 degree margin)
        const boundingBox = {
            ne: { lat: lat + 0.01, lng: lng + 0.01 },
            nw: { lat: lat + 0.01, lng: lng - 0.01 },
            se: { lat: lat - 0.01, lng: lng + 0.01 },
            sw: { lat: lat - 0.01, lng: lng - 0.01 },
        };

        if (onBoundingBoxUpdate) {
            // Create address components from Nominatim data for compatibility
            const addressComponents = [];
            if (suggestion.address) {
                const addr = suggestion.address;
                if (addr.house_number && addr.road) {
                    addressComponents.push({ types: ["street_number"], long_name: addr.house_number, short_name: addr.house_number });
                    addressComponents.push({ types: ["route"], long_name: addr.road, short_name: addr.road });
                }
                if (addr.city || addr.town || addr.village) {
                    addressComponents.push({
                        types: ["locality"],
                        long_name: addr.city || addr.town || addr.village,
                        short_name: addr.city || addr.town || addr.village,
                    });
                }
                if (addr.state) {
                    addressComponents.push({ types: ["administrative_area_level_1"], long_name: addr.state, short_name: addr.state });
                }
                if (addr.postcode) {
                    addressComponents.push({ types: ["postal_code"], long_name: addr.postcode, short_name: addr.postcode });
                }
                if (addr.country) {
                    addressComponents.push({
                        types: ["country"],
                        long_name: addr.country,
                        short_name: addr.country_code?.toUpperCase() || addr.country,
                    });
                }
            }

            // Create a place object compatible with existing onBoundingBoxUpdate function
            const placeObject = {
                formatted_address: description,
                address_components: addressComponents,
                types: ["locality"], // Default to locality type
                geometry: {
                    location: {
                        lat: lat,
                        lng: lng,
                        toJSON: () => ({ lat, lng }),
                    },
                },
            };
            onBoundingBoxUpdate(boundingBox, placeObject);
        }

        setSuggestions([]);
        setInputFocused(false); // Ensure input is unfocused after selection
    };

    const handleRemoveLocation = (location, index) => {
        const updatedLocations = selectedLocations.filter((loc, i) => i !== index);
        setSelectedLocations(updatedLocations);
        deleteSearchLocation(index);
    };

    const handleClearSearch = () => {
        setInputValue("");
        if (setAddress) setAddress("");
        if (clearSearchLocation) clearSearchLocation();
        setSuggestions([]);
    };

    return (
        <div ref={geoSearchRef} className="tw-w-full">
            <div className={className || `tw-input-location ${(isSimple || true) && "tw-p-0 tw-bg-white tw-rounded-lg "}  tw-w-full tw-h-[100%]`}>
                <div ref={inputRef} className={`input-wrapper-geosearch tw-relative  ${(isSimple || true) && "tw-bg-white tw-pl-2 tw-rounded-lg"} `}>
                    <input
                        type="text"
                        required
                        className={`${(isSimple || true) && "tw-bg-white tw-text-black tw-py-2 "} ${inputValue && inputValue.trim() ? "tw-pr-8" : ""}`}
                        value={inputValue}
                        onChange={handleInputChange}
                        onBlur={() => {
                            // Allow time for click events to process
                            setTimeout(() => {
                                setInputFocused(false);
                                setSuggestions([]);
                            }, 200);
                        }}
                        onFocus={() => setInputFocused(true)}
                        placeholder="City, Neighborhood, Zip, Address"
                    />
                    {inputValue && inputValue.trim() && (
                        <button
                            onClick={handleClearSearch}
                            className="tw-absolute tw-right-2 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 tw-bg-transparent tw-border-none tw-cursor-pointer"
                        >
                            <AiOutlineClose size={16} />
                        </button>
                    )}
                </div>
                {
                    <>
                        {loading ? (
                            <ul className="suggestions-list tw-p-5 tw-flex tw-items-center tw-justify-center tw-w-full">
                                <Balls></Balls>
                            </ul>
                        ) : (
                            <>
                                {error && (
                                    <ul className="suggestions-list">
                                        <li className="tw-p-3 tw-text-red-500 tw-text-sm tw-cursor-default">{error}</li>
                                    </ul>
                                )}
                                {noResults && !error && inputValue.trim().length > 2 && (
                                    <ul className="suggestions-list">
                                        <li className="tw-p-3 tw-text-gray-500 tw-text-sm tw-cursor-default">
                                            No addresses found. Try a different search or use format: "Street Number Street Name, City, State"
                                        </li>
                                    </ul>
                                )}
                                {suggestions.length > 0 && !loading && inputFocused && (
                                    <ul ref={suggestionsRef} className="suggestions-list ">
                                        {suggestions.map((suggestion) => (
                                            <li
                                                key={suggestion.place_id}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleSelectSuggestion(suggestion);
                                                }}
                                                style={{ cursor: "pointer", userSelect: "none" }}
                                            >
                                                {suggestion.description}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </>
                }
            </div>
        </div>
    );
};

export default GeoSearch;
